import * as React from "react";
import type { RefCallback } from "react";

import { useSettledMeasure } from "../../hooks/use-layout-settle";
import type { WidgetBodyLoadingRegistry } from "./contexts";

/**
 * AutoHeight measurement: observers report natural content px UP to the grid
 * host, which converts to whole rows. The body sizer FILLS the card rather
 * than hugging, so its own box is the slot's, not the content's — which costs
 * this hook both halves of the read: `report` has to neutralize the stretch
 * to get a natural number, and a size observer alone would never learn that a
 * number changed. Input-only: NO px concept ever reaches the rendered layout
 * from here.
 *
 * `contentRef` goes on the body sizer; `setHeaderNode` on the header row (it
 * composes the drag activator's ref, so one node both feeds the measurement
 * and anchors keyboard drag); `bodyLoadingRegistry` is published to the body
 * so mounted skeletons mark their heights as placeholders.
 */
export function useWidgetAutoHeight({
  enabled,
  cardRef,
  headerActivatorRef,
  onContentHeightChange,
}: {
  enabled: boolean;
  /** The card node: its border chrome is part of the height the grid seats. */
  cardRef: React.RefObject<HTMLDivElement | null>;
  headerActivatorRef: RefCallback<HTMLDivElement> | undefined;
  onContentHeightChange: ((px: number, opts?: { snap?: boolean }) => void) | undefined;
}): {
  contentRef: React.RefObject<HTMLDivElement | null>;
  setHeaderNode: (node: HTMLDivElement | null) => void;
  bodyLoadingRegistry: WidgetBodyLoadingRegistry;
  /** Heights are measured: `enabled` with a host listening. */
  measure: boolean;
} {
  // AutoHeight measurement: observers report natural content px UP to the grid
  // host, which converts to whole rows. The body sizer FILLS the card rather
  // than hugging (see the autoHeight body region below), so its own box is the
  // slot's, not the content's — which costs this block both halves of the
  // read: `report` has to neutralize the stretch to get a natural number, and
  // a size observer alone would never learn that a number changed. Input-only:
  // NO px concept ever reaches the rendered layout from here.
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const headerRef = React.useRef<HTMLDivElement | null>(null);
  // The header's drag spread carries its own activator `ref`, so a bare
  // `ref={headerRef}` written before it is silently clobbered — and a header
  // the report cannot see under-sizes every autoHeight tile by exactly its
  // own height. Dashboards' tall rows usually round past the loss; a tight
  // summary pitch clips. Compose the two: the node feeds the measurement AND
  // anchors keyboard drag.
  const setHeaderNode = React.useCallback(
    (node: HTMLDivElement | null) => {
      headerRef.current = node;
      headerActivatorRef?.(node);
    },
    [headerActivatorRef],
  );
  const reportRef = React.useRef(onContentHeightChange);
  reportRef.current = onContentHeightChange;
  const mutationsRef = React.useRef<MutationObserver | null>(null);
  const measure = enabled && onContentHeightChange != null;
  // Load-state ledger for the snap decision: while any `WidgetSkeleton` is
  // mounted in the body, every measured height is a placeholder height — and
  // the FIRST measurement after loading ends (or ever, for a body that mounts
  // with its data) is content settling, not a user action. Both report
  // `snap`, so the grid commits them without the FLIP spring; only changes to
  // an already-settled body (expanding a stat, adding a row) animate. Loading
  // re-arms the settle, so a refresh's skeleton→content swap stays quiet too.
  const bodyLoadingCountRef = React.useRef(0);
  const bodySettledRef = React.useRef(false);
  const bodyLoadingRegistry = React.useMemo<WidgetBodyLoadingRegistry>(
    () => ({
      enter: () => {
        bodyLoadingCountRef.current += 1;
        bodySettledRef.current = false;
      },
      exit: () => {
        bodyLoadingCountRef.current = Math.max(0, bodyLoadingCountRef.current - 1);
      },
    }),
    [],
  );
  const report = React.useCallback(() => {
    const content = contentRef.current;
    if (!content) return;
    // Read the NATURAL height: pin the sizer to `0 0 auto` for the read and
    // hand the stretch straight back. Both halves of that toggle land inside
    // one layout pass, so the observer below compares two identical heights
    // and cannot re-enter. The stretch must not survive the read in either
    // direction: grown, the sizer reports the SLOT instead of the content and
    // the card can never lose a row again; shrunk, it reports less than the
    // content while the reflow spring still holds the tile at its pre-growth
    // height, collapsing the span the growth just won.
    content.style.flex = "0 0 auto";
    const natural = content.offsetHeight;
    content.style.flex = "";
    // Those two writes are themselves mutations on the observed root, so the
    // mutation observer below would re-enter on its own measurement forever.
    // Drop what they queued. Anything queued ahead of them describes the DOM
    // this read just measured, so losing it costs a duplicate report.
    mutationsRef.current?.takeRecords();
    const snap = bodyLoadingCountRef.current > 0 || !bodySettledRef.current;
    if (bodyLoadingCountRef.current === 0) bodySettledRef.current = true;
    // The Card's top+bottom borders live inside the grid cell (border-box),
    // so a report of content + header alone under-asks by exactly that
    // chrome — a clipped descender whenever the sum lands flush on a row
    // boundary. `overflow: hidden` means no scrollbar rides this delta.
    const card = cardRef.current;
    const cardChrome = card ? card.offsetHeight - card.clientHeight : 0;
    reportRef.current?.(
      natural + (headerRef.current?.offsetHeight ?? 0) + cardChrome,
      { snap },
    );
  }, [cardRef]);
  // The settle window holds the report — this write-read-write is a forced
  // synchronous layout, and per card per animation frame it is the deepest
  // cost of the pane slide. One report runs when the window closes.
  const reportWhenSettled = useSettledMeasure(report);
  // Measure BEFORE paint on every render. A body that grows synchronously — a
  // stat expanding its table, a list swapping its rows — must reach the grid in
  // the frame that renders it: a layout effect's state update flushes before
  // the browser paints, so the row growth and the new content land together.
  // Left to the observer alone the notification arrives after the commit and
  // the card paints exactly once at the old height holding the new body.
  React.useLayoutEffect(() => {
    if (measure) reportWhenSettled();
  });
  // Two observers, because the sizer's box answers only half the question.
  // SIZE covers what no render announces: fonts, images, an async measure
  // inside the body, a width change reflowing text. It cannot cover a body
  // that SHRINKS — the sizer is stretched to the slot, so content collapsing
  // into the leftover resizes nothing, and a size observer alone lets the span
  // ratchet: every growth measured, no collapse ever. The subtree MUTATION is
  // that signal — a disclosure unmounting its rows, a list swapping them, a
  // child restyling its own height — and `report` reads the natural height
  // back through the stretch. Attributes are filtered to the two that carry
  // height; the rest of the traffic under a table body is bookkeeping, and
  // each record costs a forced layout.
  React.useLayoutEffect(() => {
    if (!measure) return;
    const content = contentRef.current;
    if (!content) return;
    const headerNode = headerRef.current;
    const sizes = new ResizeObserver(reportWhenSettled);
    sizes.observe(content);
    if (headerNode) sizes.observe(headerNode);
    // A subtree mutation storm (a list swapping every row) collapses into one
    // report per frame; a size change reports at once.
    let frame = 0;
    const reportNextFrame = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        reportWhenSettled();
      });
    };
    const mutations = new MutationObserver(reportNextFrame);
    mutationsRef.current = mutations;
    mutations.observe(content, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["style", "class"],
    });
    return () => {
      if (frame) cancelAnimationFrame(frame);
      sizes.disconnect();
      mutations.disconnect();
      mutationsRef.current = null;
    };
  }, [measure, reportWhenSettled]);

  return { contentRef, setHeaderNode, bodyLoadingRegistry, measure };
}
